import { useMemo } from 'react';

import LineChart, { GraphicalLineChartDataset } from 'components/Charts/LineChart/LineChart';
import { useTranslation } from 'react-i18next';
import { TimelineChartPoint } from 'types/CodegenReportTypes';

import { getOrderedColorSet, orderedColorSets } from 'utilities/colorHelpers';
import { Typography } from '@trackman/web-shared-components';

type Props = {
  label: string;
  trendPoints: TimelineChartPoint[];
  linePoints: TimelineChartPoint[];
};

const TimelineTrendChart = ({ label, trendPoints, linePoints }: Props) => {
  const { t } = useTranslation();

  const sortedTrendPoints = useMemo(
    () => [...trendPoints].sort((a, b) => new Date(a.x).getTime() - new Date(b.x).getTime()),
    [trendPoints]
  );
  const sortedLinePoints = useMemo(() => [...linePoints].sort((a, b) => new Date(a.x).getTime() - new Date(b.x).getTime()), [linePoints]);

  const minValue = useMemo(() => Math.min(...sortedTrendPoints.map((p) => p.y)), [sortedTrendPoints]);
  const maxValue = useMemo(() => Math.max(...sortedTrendPoints.map((p) => p.y)), [sortedTrendPoints]);

  const normalizeValue = (value) => {
    if (maxValue === minValue) return 0.5;
    return (value - minValue) / (maxValue - minValue);
  };

  const gradientScaleRange = 0.25;

  const dataSets: GraphicalLineChartDataset[] = useMemo(() => {
    if (sortedTrendPoints.length === 0 || sortedLinePoints.length === 0) {
      return [];
    }

    return [
      {
        label: label,
        color: '',
        points: sortedTrendPoints,
        drawMode: 'gradient',
        strokeWidth: 3,
        legendItem: {
          label: '',
          icon: (
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <Typography className='Legend-item-label' component='div' variant='label'>
                {t('14DayMovingAverage') + ': '}
              </Typography>
              <Typography className='Legend-item-label' component='div' variant='label'>
                {t('High')}
              </Typography>
              <div
                style={{ display: 'flex', height: 'fit-content', borderRadius: '999px', border: '1px solid #e0e0e0', overflow: 'hidden' }}
              >
                {orderedColorSets
                  .toReversed()
                  .filter((_, i) => {
                    const len = orderedColorSets.length;
                    const mid = len / 2;
                    if (len % 2 === 0) {
                      return i !== mid && i !== mid - 1;
                    }
                    return i !== Math.floor(mid);
                  })
                  .map((colorSet, i) => (
                    <span key={i} style={{ backgroundColor: colorSet.bg, width: '8px', height: '8px' }}></span>
                  ))}
              </div>
              <Typography className='Legend-item-label' component='div' variant='label'>
                {t('Low')}
              </Typography>
            </div>
          ),
        },
        getGradientStops: (point, nextPoint) => {
          const stops = [] as { offset: number; color: string }[];
          const totalSteps = 7;

          const lineSlope = (nextPoint.y - point.y) / (new Date(nextPoint.x).getTime() - new Date(point.x).getTime());
          const lineIntercept = point.y - lineSlope * new Date(point.x).getTime();

          const baseLineValue = sortedLinePoints.find((p) => p.x === point.x)?.y ?? 0;
          const nextBaseLineValue = sortedLinePoints.find((p) => p.x === nextPoint.x)?.y ?? 0;
          const baseLineSlope = (nextBaseLineValue - baseLineValue) / (new Date(nextPoint.x).getTime() - new Date(point.x).getTime());
          const baseLineIntercept = baseLineValue - baseLineSlope * new Date(point.x).getTime();

          for (let i = 0; i <= totalSteps; i++) {
            const progress = i / totalSteps;
            const currentTime = new Date(point.x).getTime() + progress * (new Date(nextPoint.x).getTime() - new Date(point.x).getTime());
            const currentY = lineSlope * currentTime + lineIntercept;
            const currentBaseLineValue = baseLineSlope * currentTime + baseLineIntercept;

            const normalizedY = normalizeValue(currentY);
            const normalizedBaseLineValue = normalizeValue(currentBaseLineValue);
            const lowerBound = normalizedBaseLineValue - gradientScaleRange;
            const upperBound = normalizedBaseLineValue + gradientScaleRange;

            const color = getOrderedColorSet(lowerBound, upperBound, normalizedY).bg;
            stops.push({ offset: progress * 100, color: color });
          }

          return stops;
        },
      },
      {
        label: t('RollingAverage'),
        color: '#414141',
        points: sortedLinePoints,
        drawMode: 'groupColor',
        legendItem: {
          label: t('RollingAverage'),
          icon: <div style={{ width: '14px', border: '2px solid #414141' }}></div>,
        },
      },
    ];
  }, [sortedTrendPoints, sortedLinePoints]);

  return <LineChart xLabel={''} yLabel={label} chartData={dataSets} dataPointVisibility='hovered' />;
};

export default TimelineTrendChart;
